/**
 * This file contains CRM standalone VUE app support for Benefit quote pages
 */
import cloneDeep from 'lodash.clonedeep';
import { mapMutations } from 'vuex';
import { formatFormDate } from '@general/js/utils/dateTimeUtils';
import { VUEX_BENEFIT_QUOTES_CLAIMS } from '@/constants/VuexConstants';
import BenefitQuotesServices from '@/services/BenefitQuotes';
import { isBuildCRM } from '@/utils';
import { isEmptyString } from '@/utils/DisplayUtils';

const CRM_PAGES = {
  LANDING: 'BenefitQuotesAndClaims',
  GABQ: 'GABQnMANC',
  RESULT: 'QuoteResultClaimSubmission',
};

let CRM_POLICY_NUMBER;
if (isBuildCRM()) {
  if (process.env.NODE_ENV === 'development') {
    // Allow developers to enter a policy # at runtime instead of hardcoding. Old policies stop working once old data is purged in OHI.
    // eslint-disable-next-line no-alert
    CRM_POLICY_NUMBER = prompt(`Enter the policy number for use in API calls to CRM. The policy must be in CRM DEV01 environment and active in OHI.`, '7736747');
  } else {
    CRM_POLICY_NUMBER = window.parent.Xrm.Page.getAttribute('dhl_membershipid').getValue();
  }
}

export default {
  props: {
    // If true, page would considered it is claims landing page. default to be false
    isClaim: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      CRM_PAGES,
      CRM_POLICY_NUMBER,
    };
  },

  computed: {
    /**
     * Provider object that's selected for display purposes
     */
    selectedProviderObject() {
      const [providerId, subProviderIdentifierType] = this.feature.step3.selectedProvider.split('-');

      if (!isEmptyString(subProviderIdentifierType)) {
        return this.refData.providers.find((provider) => {
          return provider.providerId === providerId && provider.subProviderIdentifierType === subProviderIdentifierType;
        });
      }

      return this.refData.providers.find((provider) => {
        return provider.providerId === providerId;
      });
    },
  },

  methods: {
    ...mapMutations('BenefitQuotesClaims', {
      resetVuex: VUEX_BENEFIT_QUOTES_CLAIMS.MUTATIONS.resetState,
    }),

    /**
     * emit data to parent for page navigation in CRM.
     *
     * @param {string} targetPageName name of target page. it should be one of `PAGES` object value.
     * @param {object} routeParams by default is empty object. if given, it should be object. this object will be used like route param in non CRM build.
     */
    crmNavigatePage(targetPageName, routeParams = {}) {
      window.scrollTo(0, 0);
      this.$emit('crmNavigatePage', targetPageName, routeParams);
    },

    /**
     * get quote method for GABQ review and submit step
     */
    async crmGetQuote() {
      const getQuoteApiStatusObj = {
        fail: false,
        apiInProgress: true,
      };
      this.apiStatus.loading = true;
      this.apiStatus.fail = false;
      this.$emit('getQuoteApiStatus', getQuoteApiStatusObj);

      const memberNumber = this.refData.memberNumber;
      const accessingPersonCode = this.feature.step1.member;
      const providerId = this.feature.step3.selectedProvider.split('-')[0];
      const providerFlexCode = this.selectedProviderFlexCode;
      const subProviderIdentifier = this.selectedProviderObject.subProviderIdentifier;
      const subProviderIdentifierType = this.selectedProviderObject.subProviderIdentifierType;
      const productCode = this.feature.step1.productCode;
      const services = cloneDeep(this.feature.step4.claimLines).map((service) => {
        // format for api
        return {
          serviceDate: formatFormDate(service.serviceDate),
          serviceId: service.serviceId,
          flexCode: service.flexCode,
          toothId: service.toothId,
          providerFee: Number(service.providerFee),
        };
      });

      try {
        const responseData = await BenefitQuotesServices.submitBenefitQuote(
          memberNumber,
          accessingPersonCode,
          providerId,
          providerFlexCode,
          subProviderIdentifier,
          subProviderIdentifierType,
          productCode,
          services,
        );

        // add missing provider data on responseData
        const providerData = this.refData.providers.find((provider) => {
          return provider.providerId === providerId;
        });

        responseData.provider = {
          ...responseData.provider,
          ...providerData,
          providerId,
        };

        this.crmNavigatePage(CRM_PAGES.RESULT, {
          formData: responseData,
          quoteId: responseData.benefitQuoteId,
          accessingPersonCode,
          refData: {}, // passing empty object to pass condition of `fetchData` to not call unnecessary api
        });
      } catch (error) {
        this.apiStatus.fail = false; // Normally this would be set to `true` but we only want to show one top-level error if Submit Benefit Quote is the only API that fails.
        getQuoteApiStatusObj.fail = true;
      } finally {
        this.apiStatus.loading = false;
        getQuoteApiStatusObj.apiInProgress = false;
        this.$emit('getQuoteApiStatus', getQuoteApiStatusObj);
      }
    },
  },
};
